import { createClient } from '@supabase/supabase-js'
import { gerarSlots, filtrarSlots, escolherSala } from './disponibilidade'
import { normalizarTelefone } from './validacoes'
import { mapearFaturas } from './faturas'
import { permissoesComDefaults, acoesComDefaults, estadosAgendaComDefaults, estadosRelatorioComDefaults, estadosFaturacaoComDefaults } from './permissoes'

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

// Cliente Supabase com o header x-tenant-id (lido por current_tenant_id() no RLS).
//
// IMPORTANTE: estes clientes são "inertes" do ponto de vista de autenticação
// (persistSession/autoRefreshToken/detectSessionInUrl a false). Só o cliente
// principal `supabase` gere a sessão. Caso contrário, cada cliente criado por
// pedido arrancava o seu próprio motor de auth a partilhar o mesmo storage, e
// no arranque vários tentavam renovar o token ao mesmo tempo — cada renovação
// disparava onAuthStateChange → recarregava o perfil → criava mais clientes →
// loop infinito de refresh no backoffice.
//
// O token do utilizador é injectado manualmente no header Authorization para que
// o RLS (auth.uid()) continue a funcionar nas queries autenticadas.
function clientComTenant(tenantId, accessToken) {
  const headers = { 'x-tenant-id': tenantId }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY,
    {
      global: { headers },
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    }
  )
}

async function comTenant(tenantId, fn) {
  if (!tenantId) throw new Error('tenant_id em falta')
  // Buscar o token atual ao cliente principal (única fonte de verdade da sessão).
  const { data: { session } } = await supabase.auth.getSession()
  return fn(clientComTenant(tenantId, session?.access_token))
}

// -----------------------------------------------
// Perfil do utilizador autenticado
// -----------------------------------------------
// Usa o cliente com header x-tenant-id (não o set_tenant RPC): o set_config
// do set_tenant é local à transação e não sobrevive entre pedidos do PostgREST,
// o que fazia o RLS de `perfis` devolver 0 linhas (406) e prender o login.
export async function getPerfil(tenantId, userId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('perfis')
      .select('*, profissionais(id, nome, cor_agenda)')
      .eq('id', userId)
      .maybeSingle()
    if (error) throw error
    return data
  })
}

// -----------------------------------------------
// Configurações
// -----------------------------------------------
export async function getConfiguracoes(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.from('configuracoes').select('*')
    if (error) throw error
    return Object.fromEntries(data.map(r => [r.chave, r.valor]))
  })
}

export async function updateConfiguracao(tenantId, chave, valor) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('configuracoes')
      .upsert(
        { chave, valor, updated_at: new Date().toISOString(), tenant_id: tenantId },
        { onConflict: 'tenant_id,chave' }
      )
    if (error) throw error
  })
}

// Nº de linhas por página das listagens paginadas do backoffice (Clientes,
// detalhe dos Relatórios…). Configurável em Config Admin (`paginacao_tamanho`),
// com fallback a 20 se a chave não existir ou for inválida.
export async function getTamanhoPaginacao(tenantId) {
  const cfg = await getConfiguracoes(tenantId)
  const n = parseInt(cfg.paginacao_tamanho, 10)
  return n > 0 ? n : 20
}

// Ordem das categorias de serviços (lista de nomes em JSON, na chave
// `ordem_categorias`). Ver `src/lib/categorias.js` para a aplicação da ordem.
export async function salvarOrdemCategorias(tenantId, nomes) {
  return updateConfiguracao(tenantId, 'ordem_categorias', JSON.stringify(nomes))
}

// Ordem dos profissionais na Agenda do Dia (lista de ids em JSON, na chave
// `ordem_profissionais`). Ver `src/lib/profissionais.js` para a aplicação da ordem.
export async function salvarOrdemProfissionais(tenantId, ids) {
  return updateConfiguracao(tenantId, 'ordem_profissionais', JSON.stringify(ids))
}

// Permissões do backoffice por papel: acesso a páginas (`permissoes_paginas`) e a
// ações/funcionalidades (`permissoes_acoes`). Devolve sempre mapas completos.
export async function getPermissoes(tenantId) {
  const cfg = await getConfiguracoes(tenantId)
  const parse = (s) => { try { return s ? JSON.parse(s) : null } catch { return null } }
  return {
    paginas: permissoesComDefaults(parse(cfg.permissoes_paginas)),
    acoes: acoesComDefaults(parse(cfg.permissoes_acoes)),
    estadosAgenda: estadosAgendaComDefaults(parse(cfg.permissoes_estados_agenda)),
    estadosRelatorio: estadosRelatorioComDefaults(parse(cfg.permissoes_estados_relatorio)),
    estadosFaturacao: estadosFaturacaoComDefaults(parse(cfg.permissoes_estados_faturacao)),
  }
}

export async function updatePermissoes(tenantId, { paginas, acoes, estadosAgenda, estadosRelatorio, estadosFaturacao }) {
  await Promise.all([
    updateConfiguracao(tenantId, 'permissoes_paginas', JSON.stringify(paginas)),
    updateConfiguracao(tenantId, 'permissoes_acoes', JSON.stringify(acoes)),
    updateConfiguracao(tenantId, 'permissoes_estados_agenda', JSON.stringify(estadosAgenda)),
    updateConfiguracao(tenantId, 'permissoes_estados_relatorio', JSON.stringify(estadosRelatorio)),
    updateConfiguracao(tenantId, 'permissoes_estados_faturacao', JSON.stringify(estadosFaturacao)),
  ])
}

// -----------------------------------------------
// Serviços
// -----------------------------------------------
export async function getServicos(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('servicos')
      .select('*')
      .eq('ativo', true)
      .eq('visivel_publico', true)
      .order('categoria')
      .order('ordem')
    if (error) throw error
    return data
  })
}

export async function getServicosAdmin(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('servicos')
      .select('*')
      .order('categoria')
      .order('ordem')
    if (error) throw error
    return data
  })
}

export async function createServico(tenantId, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('servicos')
      .insert([{ ...dados, tenant_id: tenantId }])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

export async function updateServico(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('servicos')
      .update(dados)
      .eq('id', id)
    if (error) throw error
  })
}

// Upload da média de apresentação de um serviço (imagem ou vídeo) para o
// bucket público `servicos-midia`. O ficheiro é guardado sob a pasta do tenant
// (1.º segmento do caminho) — exigido pelas políticas RLS de Storage (053).
// Devolve { url, tipo } onde tipo é 'imagem' ou 'video' (deduzido do MIME).
export async function uploadMidiaServico(tenantId, file) {
  return comTenant(tenantId, async (db) => {
    const tipo = file.type.startsWith('video/') ? 'video'
      : file.type.startsWith('image/') ? 'imagem'
      : null
    if (!tipo) throw new Error('Ficheiro inválido: escolha uma imagem ou um vídeo.')

    const ext = (file.name.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin'
    const caminho = `${tenantId}/${crypto.randomUUID()}.${ext}`

    const { error } = await db.storage
      .from('servicos-midia')
      .upload(caminho, file, { cacheControl: '31536000', upsert: false, contentType: file.type })
    if (error) throw error

    const { data } = db.storage.from('servicos-midia').getPublicUrl(caminho)
    return { url: data.publicUrl, tipo }
  })
}

export async function getCategoriasUsadas(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('servicos')
      .select('categoria')
      .order('categoria')
    if (error) throw error
    const unicas = [...new Set((data || []).map(s => s.categoria))]
    return unicas
  })
}

// -----------------------------------------------
// Profissionais
// -----------------------------------------------
export async function getProfissionais(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('profissionais')
      .select('*')
      .eq('ativo', true)
      .order('nome')
    if (error) throw error
    return data
  })
}

export async function getProfissionaisAdmin(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('profissionais')
      .select('*, perfis(id, email, ativo, role)')
      .order('nome')
    if (error) throw error
    return data
  })
}

export async function createProfissional(tenantId, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('profissionais')
      .insert([{ ...dados, tenant_id: tenantId }])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

export async function updateProfissional(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('profissionais')
      .update(dados)
      .eq('id', id)
    if (error) throw error
  })
}

// Upload da fotografia de um profissional (já recortada no cliente) para o
// bucket público `profissionais-fotos`. Ver `uploadMidiaServico` para o
// mesmo padrão. Recebe um Blob (ex.: resultado do canvas de recorte).
export async function uploadFotoProfissional(tenantId, blob) {
  return comTenant(tenantId, async (db) => {
    const caminho = `${tenantId}/${crypto.randomUUID()}.jpg`

    const { error } = await db.storage
      .from('profissionais-fotos')
      .upload(caminho, blob, { cacheControl: '31536000', upsert: false, contentType: 'image/jpeg' })
    if (error) throw error

    const { data } = db.storage.from('profissionais-fotos').getPublicUrl(caminho)
    return data.publicUrl
  })
}

// Profissionais activos que executam um determinado serviço
export async function getProfissionaisDoServico(tenantId, servicoId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('profissional_servicos')
      .select('profissionais!inner(*)')
      .eq('servico_id', servicoId)
      .eq('profissionais.ativo', true)
    if (error) throw error
    return (data || [])
      .map(r => r.profissionais)
      .sort((a, b) => a.nome.localeCompare(b.nome))
  })
}

// -----------------------------------------------
// Profissional-Serviços
// -----------------------------------------------

// Devolve um mapa { profissionalId: [servicoId, ...] }
export async function getProfissionalServicosMap(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('profissional_servicos')
      .select('profissional_id, servico_id')
    if (error) throw error
    const mapa = {}
    for (const r of data || []) {
      if (!mapa[r.profissional_id]) mapa[r.profissional_id] = []
      mapa[r.profissional_id].push(r.servico_id)
    }
    return mapa
  })
}

// Substitui o conjunto de serviços de um profissional pelos IDs fornecidos
export async function setServicosDoProfissional(tenantId, profissionalId, servicoIds) {
  return comTenant(tenantId, async (db) => {
    const { error: delErr } = await db
      .from('profissional_servicos')
      .delete()
      .eq('profissional_id', profissionalId)
    if (delErr) throw delErr

    if (!servicoIds || servicoIds.length === 0) return

    const linhas = servicoIds.map(servico_id => ({
      profissional_id: profissionalId,
      servico_id,
      tenant_id: tenantId,
    }))
    const { error: insErr } = await db
      .from('profissional_servicos')
      .insert(linhas)
    if (insErr) throw insErr
  })
}

// -----------------------------------------------
// Slots disponíveis para um dia e profissional
// -----------------------------------------------
export async function getSlotsDisponiveis(tenantId, profissionalId, data, duracaoMin, config, categoria = null, ignorarAntecedencia = false) {
  return comTenant(tenantId, async (db) => {
    const { hora_abertura, hora_fecho, slot_minutos } = config

    const slots = gerarSlots(data, hora_abertura, hora_fecho, slot_minutos, duracaoMin)

    const inicioDia = new Date(data)
    inicioDia.setHours(0, 0, 0, 0)
    const fimDia = new Date(data)
    fimDia.setHours(23, 59, 59, 999)

    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!UUID_RE.test(profissionalId)) throw new Error('profissionalId inválido')

    // Ocupação do dia via função SECURITY DEFINER (o role anon não tem SELECT
    // em agendamentos; sem isto o site público via todos os horários livres).
    const { data: ocupacao, error: eOc } = await db.rpc('get_ocupacao_dia', {
      p_tenant_id: tenantId,
      p_inicio: inicioDia.toISOString(),
      p_fim: fimDia.toISOString(),
    })
    if (eOc) throw eOc

    const agendamentos = (ocupacao || []).filter(o => o.profissional_id === profissionalId)

    const { data: bloqueios, error: e2 } = await db
      .from('bloqueios')
      .select('data_hora_inicio, data_hora_fim')
      .or(`profissional_id.eq.${profissionalId},profissional_id.is.null`)
      // Sobreposição com o dia (não contenção) — um bloqueio de vários dias
      // começa antes e acaba depois do dia pedido.
      .lt('data_hora_inicio', fimDia.toISOString())
      .gt('data_hora_fim', inicioDia.toISOString())

    if (e2) throw e2

    const ocupados = [...(agendamentos || []), ...(bloqueios || [])]

    let capacidade = Infinity
    let agendamentosCategoria = []
    if (categoria) {
      const { data: salas, error: eS } = await db
        .from('salas')
        .select('id')
        .eq('categoria', categoria)
      if (eS) throw eS
      if (salas && salas.length > 0) {
        capacidade = salas.length
        agendamentosCategoria = (ocupacao || []).filter(o => o.categoria === categoria)
      }
    }

    const antecedenciaMs = (parseInt(config.antecedencia_horas) || 0) * 60 * 60 * 1000

    let almocoInicio = null, almocoFim = null
    if (config.almoco_ativo === 'true' && config.hora_almoco_inicio && config.hora_almoco_fim) {
      const [haI, maI] = config.hora_almoco_inicio.split(':').map(Number)
      const [haF, maF] = config.hora_almoco_fim.split(':').map(Number)
      almocoInicio = new Date(data); almocoInicio.setHours(haI, maI, 0, 0)
      almocoFim = new Date(data); almocoFim.setHours(haF, maF, 0, 0)
    }

    return filtrarSlots(slots, {
      duracaoMin,
      ocupados,
      agora: ignorarAntecedencia ? new Date(0) : new Date(),
      antecedenciaMs: ignorarAntecedencia ? 0 : antecedenciaMs,
      almocoInicio,
      almocoFim,
      capacidade,
      agendamentosCategoria,
    })
  })
}

// Devolve a união de slots disponíveis de todos os profissionais passados,
// mais um mapa { slotISO → [profissionalId, ...] } para escolher o profissional
// na confirmação.
export async function getSlotsDisponiveisTodos(tenantId, profissionais, data, duracaoMin, config, categoria = null) {
  return comTenant(tenantId, async (db) => {
    const { hora_abertura, hora_fecho, slot_minutos } = config

    const inicioDia = new Date(data); inicioDia.setHours(0, 0, 0, 0)
    const fimDia = new Date(data); fimDia.setHours(23, 59, 59, 999)

    const { data: ocupacao, error: eOc } = await db.rpc('get_ocupacao_dia', {
      p_tenant_id: tenantId,
      p_inicio: inicioDia.toISOString(),
      p_fim: fimDia.toISOString(),
    })
    if (eOc) throw eOc

    const { data: bloqueiosGlobais, error: eBG } = await db
      .from('bloqueios')
      .select('data_hora_inicio, data_hora_fim, profissional_id')
      // Sobreposição com o dia (não contenção) — um bloqueio de vários dias
      // começa antes e acaba depois do dia pedido.
      .lt('data_hora_inicio', fimDia.toISOString())
      .gt('data_hora_fim', inicioDia.toISOString())
    if (eBG) throw eBG

    let capacidade = Infinity
    let agendamentosCategoria = []
    if (categoria) {
      const { data: salas, error: eS } = await db
        .from('salas').select('id').eq('categoria', categoria)
      if (eS) throw eS
      if (salas && salas.length > 0) {
        capacidade = salas.length
        agendamentosCategoria = (ocupacao || []).filter(o => o.categoria === categoria)
      }
    }

    const antecedenciaMs = (parseInt(config.antecedencia_horas) || 0) * 60 * 60 * 1000

    let almocoInicio = null, almocoFim = null
    if (config.almoco_ativo === 'true' && config.hora_almoco_inicio && config.hora_almoco_fim) {
      const [haI, maI] = config.hora_almoco_inicio.split(':').map(Number)
      const [haF, maF] = config.hora_almoco_fim.split(':').map(Number)
      almocoInicio = new Date(data); almocoInicio.setHours(haI, maI, 0, 0)
      almocoFim = new Date(data); almocoFim.setHours(haF, maF, 0, 0)
    }

    const slots = gerarSlots(data, hora_abertura, hora_fecho, slot_minutos, duracaoMin)

    // mapa slotISO → [profissionalId] com pelo menos um livre
    const mapaProfissionais = {}

    for (const prof of profissionais) {
      const agendamentosProf = (ocupacao || []).filter(o => o.profissional_id === prof.id)
      const bloqueiosProf = (bloqueiosGlobais || []).filter(
        b => b.profissional_id === prof.id || b.profissional_id === null
      )
      const ocupados = [...agendamentosProf, ...bloqueiosProf]

      const livres = filtrarSlots(slots, {
        duracaoMin,
        ocupados,
        agora: new Date(),
        antecedenciaMs,
        almocoInicio,
        almocoFim,
        capacidade,
        agendamentosCategoria,
      })

      for (const s of livres) {
        const iso = s.toISOString()
        if (!mapaProfissionais[iso]) mapaProfissionais[iso] = []
        mapaProfissionais[iso].push(prof.id)
      }
    }

    // slots ordenados com o mapa anexado
    const slotsUniao = Object.keys(mapaProfissionais)
      .sort()
      .map(iso => new Date(iso))

    slotsUniao._profissionaisPorSlot = mapaProfissionais
    return slotsUniao
  })
}

// -----------------------------------------------
// Criar agendamento
// -----------------------------------------------
export async function criarAgendamento(tenantId, dados, perfilNome) {
  return comTenant(tenantId, async (db) => {
    const payload = { ...dados, tenant_id: tenantId, origem: 'backoffice' }
    if (perfilNome) payload.atualizado_por = perfilNome
    if (payload.cliente_telefone) payload.cliente_telefone = normalizarTelefone(payload.cliente_telefone)

    // Bloquear se houver uma marcação pendente (a aguardar pagamento online) a
    // segurar este horário — mesmo profissional ou mesma sala.
    const { data: pendExpira, error: ePend } = await db.rpc('verificar_pendente_conflito', {
      p_tenant_id: tenantId,
      p_profissional_id: payload.profissional_id || null,
      p_sala_id: payload.sala_id || null,
      p_inicio: payload.data_hora_inicio,
      p_fim: payload.data_hora_fim,
    })
    if (ePend) throw ePend
    if (pendExpira) {
      const hora = new Date(pendExpira).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
      throw new Error(`Este horário está reservado a aguardar pagamento online (expira às ${hora}). Tente novamente depois dessa hora.`)
    }

    // Verificar conflito de sala antes de inserir
    if (payload.sala_id) {
      const { data: ocupada, error: eS } = await db.rpc('verificar_conflito_sala', {
        p_tenant_id: tenantId,
        p_sala_id:   payload.sala_id,
        p_inicio:    payload.data_hora_inicio,
        p_fim:       payload.data_hora_fim,
      })
      if (eS) throw eS
      if (ocupada) throw new Error('A sala já está ocupada neste horário.')
    }

    const { data, error } = await db
      .from('agendamentos')
      .insert([payload])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

// Cria a marcação a partir do site público (visitante anónimo) via função
// SECURITY DEFINER. O role `anon` não tem SELECT em clientes/agendamentos (por
// privacidade), por isso o upsert/insert com RETURNING falhava o RLS. A função
// valida o tenant, faz upsert do cliente e insere a marcação no servidor,
// devolvendo apenas o id da marcação.
// Cria a marcação via Edge Function pública (valida Turnstile + rate limit
// por IP e só depois chama a RPC SQL). A RPC já não é acessível ao anon
// diretamente (migração 042), por isso este é o único caminho.
export async function criarMarcacaoPublica(tenantId, cliente, agendamento, turnstileToken) {
  const clienteNorm = { ...cliente, telefone: normalizarTelefone(cliente.telefone) }
  const agendamentoNorm = { ...agendamento, cliente_telefone: normalizarTelefone(agendamento.cliente_telefone) }

  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/criar_marcacao_publica`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({
        tenant_id: tenantId,
        cliente: clienteNorm,
        agendamento: agendamentoNorm,
        turnstile_token: turnstileToken,
      }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao criar a marcação')
  return result.id
}

// Inicia o pagamento do sinal via Stripe Checkout (Edge Function pública).
// Reserva o slot numa pendente e devolve o URL de checkout; a marcação real só
// é criada após o pagamento (pela função stripe_webhook).
export async function iniciarPagamento(tenantId, cliente, agendamento, turnstileToken) {
  const clienteNorm = { ...cliente, telefone: normalizarTelefone(cliente.telefone) }
  const agendamentoNorm = { ...agendamento, cliente_telefone: normalizarTelefone(agendamento.cliente_telefone) }

  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/iniciar_pagamento`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({
        tenant_id: tenantId,
        cliente: clienteNorm,
        agendamento: agendamentoNorm,
        turnstile_token: turnstileToken,
        origin: window.location.origin,
      }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao iniciar o pagamento')
  return result.checkout_url
}

// Verifica o resultado de um pagamento pela Stripe Checkout Session:
//   'confirmado'  → a marcação foi criada
//   'processando' → ainda à espera do webhook do Stripe
//   'falhado'     → o slot ficou indisponível e o sinal foi devolvido
export async function getEstadoPagamento(tenantId, sessionId) {
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/estado_pagamento`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({ tenant_id: tenantId, session_id: sessionId }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao verificar o pagamento')
  return result.estado
}

// -----------------------------------------------
// Stripe Connect — onboarding das contas dos salões (só Super Admin)
// -----------------------------------------------

// Chama a Edge Function stripe_onboarding (autenticada com o token do utilizador).
async function chamarOnboarding(tenantId, acao) {
  const { data: sessao } = await supabase.auth.getSession()
  const token = sessao?.session?.access_token
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe_onboarding`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({ tenant_id: tenantId, acao, origin: window.location.origin }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro no onboarding Stripe')
  return result
}

// Gera o Account Link para o salão completar o onboarding Stripe Connect.
// Devolve o URL para onde redirecionar.
export async function stripeConnectLink(tenantId) {
  const { url } = await chamarOnboarding(tenantId, 'link')
  return url
}

// Consulta o Stripe e actualiza o estado (charges_enabled). Usar ao regressar
// do onboarding. Devolve { ligado, completo }.
export async function stripeConnectAtualizarEstado(tenantId) {
  return chamarOnboarding(tenantId, 'estado')
}

// Lê o estado de ligação Connect a partir da BD (sem consultar o Stripe).
// Devolve { ligado, completo }.
export async function stripeConnectEstado(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('stripe_connect_estado', { p_tenant_id: tenantId })
    if (error) throw error
    const row = Array.isArray(data) ? data[0] : data
    return { ligado: !!row?.ligado, completo: !!row?.completo }
  })
}

// -----------------------------------------------
// Vendus — faturação por tenant (só Super Admin)
// -----------------------------------------------

// Chama uma Edge Function Vendus autenticada com o token do utilizador.
async function chamarVendus(fn, tenantId, body = {}) {
  const { data: sessao } = await supabase.auth.getSession()
  const token = sessao?.session?.access_token
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${fn}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({ tenant_id: tenantId, ...body }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro na operação Vendus')
  return result
}

// Lê o estado da ligação Vendus (sem expor a key). Devolve { ligado }.
export async function vendusEstado(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('vendus_estado', { p_tenant_id: tenantId })
    if (error) throw error
    const row = Array.isArray(data) ? data[0] : data
    return { ligado: !!row?.ligado }
  })
}

// Grava (ou limpa, se vazia) a API key Vendus do salão. Devolve { ligado }.
export async function vendusGuardarKey(tenantId, apiKey) {
  return chamarVendus('vendus_guardar_key', tenantId, { api_key: apiKey })
}

// Testa a ligação à conta Vendus sem emitir documento. Devolve { ok, detalhe? }.
export async function vendusTestar(tenantId) {
  return chamarVendus('vendus_testar', tenantId)
}

// Lista os meios de pagamento da conta Vendus do salão (para associar aos
// métodos internos). Devolve { ok, metodos: [{ id, title, type }] }.
export async function vendusMetodosPagamento(tenantId) {
  return chamarVendus('vendus_metodos_pagamento', tenantId)
}

// (Re)obtém o URL público do PDF de uma fatura já emitida e grava-o na marcação.
// Útil quando a emissão não trouxe o link do PDF. Devolve { ok, pdfUrl }.
export async function obterFaturaPdf(tenantId, agendamentoId) {
  return chamarVendus('obter_fatura_pdf', tenantId, { agendamento_id: agendamentoId })
}

// Descarrega os BYTES do PDF (o "talão") de uma fatura já emitida e devolve um
// objectURL pronto a abrir/descarregar no browser. A key nunca é exposta: o PDF
// vem por proxy da Edge Function. Devolve { url, revogar } — chamar revogar()
// depois de usar para libertar o objectURL.
//
// `ref` identifica a fatura: { agendamentoId } (legado) ou { faturaId } (modelo
// de parcelas, id do documento Vendus em pagamentos.fatura_id).
export async function descarregarFaturaPdf(tenantId, ref, formato) {
  const { data: sessao } = await supabase.auth.getSession()
  const token = sessao?.session?.access_token
  // Compatibilidade: aceita também uma string (agendamentoId) como 2º argumento.
  const corpoRef = typeof ref === 'string'
    ? { agendamento_id: ref }
    : (ref?.faturaId ? { fatura_id: ref.faturaId } : { agendamento_id: ref?.agendamentoId })
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/descarregar_fatura_pdf`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'x-tenant-id': tenantId,
      },
      // formato='talao' → recibo térmico (escpos); ausente → PDF A4 (fatura).
      body: JSON.stringify({ tenant_id: tenantId, ...corpoRef, formato }),
    }
  )
  if (!resp.ok) {
    // Erro vem como JSON { error }.
    let msg = 'Não foi possível obter o PDF'
    try { msg = (await resp.json()).error || msg } catch { /* corpo não-JSON */ }
    throw new Error(msg)
  }
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  return { url, revogar: () => URL.revokeObjectURL(url) }
}

// -----------------------------------------------
// Agendamentos por intervalo (para backoffice)
// -----------------------------------------------
// `incluirCancelados`: por omissão exclui-se (é o que interessa para aviso de
// SOBREPOSIÇÃO — uma marcação cancelada não ocupa o horário). A Agenda do Dia
// precisa do oposto: traz TUDO e deixa o filtro por perfil (estadosVisiveisAgenda)
// decidir quem vê o quê — como a Agenda do Mês já faz (getAgendamentosMes).
// Sem isto, um Super Admin nunca via marcações canceladas na Agenda do Dia,
// porque a exclusão acontecia aqui, antes de a permissão ter hipótese de agir.
export async function getAgendamentos(tenantId, inicio, fim, { incluirCancelados = false } = {}) {
  return comTenant(tenantId, async (db) => {
    let q = db
      .from('agendamentos')
      .select(`*, servicos(nome, duracao_min), profissionais(nome, cor_agenda), salas(nome), clientes(telefone)`)
      .gte('data_hora_inicio', inicio)
      .lte('data_hora_inicio', fim)
      .order('data_hora_inicio')
    if (!incluirCancelados) q = q.neq('estado', 'cancelado')
    const { data, error } = await q
    if (error) throw error
    return data
  })
}

// Detalhe de uma marcação (com serviço, profissional, sala e cliente)
export async function getAgendamento(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('agendamentos')
      .select(`*, servicos(nome, duracao_min, categoria), profissionais(id, nome, cor_agenda), salas(nome), clientes(id, nome, telefone, email, data_nascimento, nif)`)
      .eq('id', id)
      .maybeSingle()
    if (error) throw error
    return data
  })
}

// Marcações de um período para RELATÓRIOS — todos os estados, com valores.
// Devolve { agendamentos, pagamentos }, tal como getAgendamentosParaCobrar: o
// que foi pago vive na tabela `pagamentos` (migração 060), não em colunas da
// própria marcação — sem as parcelas, valoresAgendamento() cai no legado
// (sinal_pago/restante_pago), que a 060 zerou para todo o tenant. Sem isto o
// relatório mostrava sempre 0 € recebido e nenhum método de pagamento.
export async function getAgendamentosRelatorio(tenantId, inicioISO, fimISO) {
  return comTenant(tenantId, async (db) => {
    const TAMANHO_PAGINA = 1000
    let agendamentos = []
    let pagina = 0
    while (true) {
      const desde = pagina * TAMANHO_PAGINA
      const ate = desde + TAMANHO_PAGINA - 1
      const { data, error } = await db
        .from('agendamentos')
        .select('*, servicos(nome, preco), profissionais(nome, cor_agenda)')
        .gte('data_hora_inicio', inicioISO)
        .lte('data_hora_inicio', fimISO)
        .order('data_hora_inicio')
        .range(desde, ate)
      if (error) throw error
      agendamentos = agendamentos.concat(data)
      if (data.length < TAMANHO_PAGINA) break
      pagina += 1
    }
    // Por lotes: com muitas marcações no período, um só `.in(...)` com todos
    // os ids faz o URL do GET rebentar (ver getAgendamentosParaMarcarPagos).
    const ids = agendamentos.map(a => a.id)
    const LOTE_IN = 200
    let pagamentos = []
    for (let i = 0; i < ids.length; i += LOTE_IN) {
      const bloco = ids.slice(i, i + LOTE_IN)
      const { data: pags, error: e2 } = await db
        .from('pagamentos')
        .select('*')
        .in('agendamento_id', bloco)
      if (e2) throw e2
      if (pags) pagamentos = pagamentos.concat(pags)
    }
    return { agendamentos, pagamentos }
  })
}

// Atualiza os valores (preço total e sinal) de uma marcação.
export async function updateValoresAgendamento(tenantId, id, { preco_total, sinal_valor }) {
  const limpar = (v) => (v === '' || v == null ? null : Number(v))
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('agendamentos')
      .update({ preco_total: limpar(preco_total), sinal_valor: limpar(sinal_valor), atualizado_em: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  })
}

// Marca/desmarca o valor restante como pago (liquidado no salão), com método.
export async function updateRestantePago(tenantId, id, pago, metodo = null) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('agendamentos')
      .update({
        restante_pago: !!pago,
        restante_pago_em: pago ? new Date().toISOString() : null,
        restante_metodo: pago ? (metodo || null) : null,
        atualizado_em: new Date().toISOString(),
      })
      .eq('id', id)
    if (error) throw error
  })
}

// Notificações enviadas para uma marcação
export async function getNotificacoesAgendamento(tenantId, agendamentoId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('notificacoes_enviadas')
      .select('id, tipo, canal, estado, detalhe, enviado_em')
      .eq('agendamento_id', agendamentoId)
      .order('enviado_em')
    if (error) throw error
    return data || []
  })
}

// Atualiza o estado de uma marcação (confirmado/concluido/falta/cancelado)
export async function updateEstadoAgendamento(tenantId, id, estado, perfilNome) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('agendamentos')
      .update({ estado, atualizado_em: new Date().toISOString(), atualizado_por: perfilNome || null })
      .eq('id', id)
    if (error) throw error
  })
}

// Verifica se uma sala está ocupada num determinado intervalo (excluindo o agendamento actual)
export async function verificarConflitoSala(tenantId, salaId, inicio, fim, excluirId = null) {
  return comTenant(tenantId, async (db) => {
    const { data: ocupada, error } = await db.rpc('verificar_conflito_sala', {
      p_tenant_id:  tenantId,
      p_sala_id:    salaId,
      p_inicio:     inicio,
      p_fim:        fim,
      p_excluir_id: excluirId,
    })
    if (error) throw error
    return !!ocupada
  })
}

// Atualiza profissional e/ou sala de uma marcação (com verificação de conflito de sala)
// forcar=true salta a verificação (utilizador confirmou o conflito no frontend)
export async function updateAgendamento(tenantId, id, campos, perfilNome, forcar = false) {
  return comTenant(tenantId, async (db) => {
    if (campos.sala_id && !forcar) {
      const ag = await getAgendamento(tenantId, id)
      const { data: ocupada, error: eS } = await db.rpc('verificar_conflito_sala', {
        p_tenant_id:  tenantId,
        p_sala_id:    campos.sala_id,
        p_inicio:     ag.data_hora_inicio,
        p_fim:        ag.data_hora_fim,
        p_excluir_id: id,
      })
      if (eS) throw eS
      if (ocupada) throw new Error('A sala já está ocupada neste horário.')
    }
    const { error } = await db
      .from('agendamentos')
      .update({ ...campos, atualizado_em: new Date().toISOString(), atualizado_por: perfilNome || null })
      .eq('id', id)
    if (error) throw error
  })
}

// Remarca uma marcação (nova data/hora e, opcionalmente, novo profissional) e
// regista o histórico numa só transação (RPC `remarcar_agendamento`, migração
// 060). É a MESMA marcação — o sinal, notas e fatura mantêm-se; só muda o
// horário/profissional. Não bloqueia por conflito (o frontend mostra o aviso
// não-bloqueante). `notificar` fica registado no histórico mas NÃO envia nada
// nesta fase. Devolve a marcação atualizada.
export async function remarcarAgendamento(tenantId, id, { inicio, fim, profissionalId, motivo, notificar }, perfilNome) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('remarcar_agendamento', {
      p_tenant_id: tenantId,
      p_agendamento_id: id,
      p_novo_inicio: inicio,
      p_novo_fim: fim,
      p_novo_prof: profissionalId || null,
      p_motivo: motivo || null,
      p_notificar: !!notificar,
      p_feito_por: perfilNome || null,
    })
    if (error) throw error
    return Array.isArray(data) ? data[0] : data
  })
}

// Histórico de remarcações de uma marcação (mais recente primeiro).
export async function getHistoricoAgendamento(tenantId, agendamentoId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('agendamentos_historico')
      .select('*, de_prof:de_profissional(nome), para_prof:para_profissional(nome)')
      .eq('agendamento_id', agendamentoId)
      .order('criado_em', { ascending: false })
    if (error) throw error
    return data || []
  })
}

// Parcelas de pagamento (sinal|restante) de uma marcação (modelo da migração 060).
export async function getPagamentosDoAgendamento(tenantId, agendamentoId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('pagamentos')
      .select('*')
      .eq('agendamento_id', agendamentoId)
    if (error) throw error
    return data || []
  })
}

// Cria/atualiza uma parcela (sinal|restante) de uma marcação. Faz upsert pela
// chave (agendamento_id, tipo). Usado no detalhe (marcar sinal/restante como
// pago com método) e na Nova Marcação (sinal cobrado no ato). Devolve a parcela.
export async function upsertParcela(tenantId, agendamentoId, tipo, { valor, metodo, origem, pago }) {
  return comTenant(tenantId, async (db) => {
    const row = {
      tenant_id: tenantId,
      agendamento_id: agendamentoId,
      tipo,
      atualizado_em: new Date().toISOString(),
    }
    if (valor !== undefined) row.valor = valor === '' || valor == null ? 0 : Number(valor)
    if (metodo !== undefined) row.metodo = metodo || null
    if (origem !== undefined) row.origem = origem || 'salao'
    if (pago !== undefined) {
      row.pago = !!pago
      row.pago_em = pago ? new Date().toISOString() : null
    }
    const { data, error } = await db
      .from('pagamentos')
      .upsert(row, { onConflict: 'agendamento_id,tipo' })
      .select()
      .single()
    if (error) throw error
    return data
  })
}

// -----------------------------------------------
// Agendamentos por mês (calendário mensal)
// -----------------------------------------------
export async function getAgendamentosMes(tenantId, ano, mes) {
  return comTenant(tenantId, async (db) => {
    const inicio = new Date(ano, mes - 1, 1)
    const fim = new Date(ano, mes, 0, 23, 59, 59, 999)
    // Traz o estado para o cliente poder repartir por estado e aplicar a
    // visibilidade por perfil (a exclusão de cancelados deixou de ser hardcoded).
    // Paginado em blocos de 1000 — um mês cheio ultrapassa o limite por
    // omissão do Supabase/PostgREST, o que fazia desaparecer dias inteiros.
    const TAMANHO_PAGINA = 1000
    let todos = []
    let pagina = 0
    while (true) {
      const desde = pagina * TAMANHO_PAGINA
      const ate = desde + TAMANHO_PAGINA - 1
      const { data, error } = await db
        .from('agendamentos')
        .select('data_hora_inicio, profissional_id, estado')
        .gte('data_hora_inicio', inicio.toISOString())
        .lte('data_hora_inicio', fim.toISOString())
        .order('data_hora_inicio')
        .range(desde, ate)
      if (error) throw error
      todos = todos.concat(data)
      if (data.length < TAMANHO_PAGINA) break
      pagina += 1
    }
    return todos
  })
}

// -----------------------------------------------
// Bloqueios
// -----------------------------------------------
export async function getBloqueios(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('bloqueios')
      .select('*, profissionais(nome, cor_agenda)')
      .order('data_hora_inicio')
    if (error) throw error
    return data
  })
}

export async function getBloqueiosDoDia(tenantId, inicio, fim) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('bloqueios')
      .select('*, profissionais(nome, cor_agenda)')
      .lt('data_hora_inicio', fim)
      .gt('data_hora_fim', inicio)
      .order('data_hora_inicio')
    if (error) throw error
    return data
  })
}

export async function createBloqueio(tenantId, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('bloqueios')
      .insert([{ ...dados, tenant_id: tenantId }])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

export async function updateBloqueio(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('bloqueios')
      .update(dados)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  })
}

// Marcações (não canceladas) que se sobrepõem a um período. Se profissionalId
// for null, considera todas as profissionais (ex.: bloqueio do espaço inteiro).
// Usado para avisar o utilizador ao criar um bloqueio sobre marcações existentes.
export async function getAgendamentosNoPeriodo(tenantId, inicioISO, fimISO, profissionalId = null) {
  return comTenant(tenantId, async (db) => {
    let q = db
      .from('agendamentos')
      .select('id, data_hora_inicio, data_hora_fim, cliente_nome, cliente_telefone, servicos(nome), profissionais(nome, cor_agenda)')
      .neq('estado', 'cancelado')
      .lt('data_hora_inicio', fimISO)
      .gt('data_hora_fim', inicioISO)
      .order('data_hora_inicio')
    if (profissionalId) q = q.eq('profissional_id', profissionalId)
    const { data, error } = await q
    if (error) throw error
    return data || []
  })
}

export async function deleteBloqueio(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db.from('bloqueios').delete().eq('id', id)
    if (error) throw error
  })
}

export async function deleteAgendamento(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db.from('agendamentos').delete().eq('id', id)
    if (error) throw error
  })
}

// -----------------------------------------------
// Clientes
// -----------------------------------------------
export async function upsertCliente(tenantId, { nome, telefone, email, data_nascimento, nif }) {
  return comTenant(tenantId, async (db) => {
    const dados = { nome, telefone: normalizarTelefone(telefone), tenant_id: tenantId }
    if (email) dados.email = email
    if (data_nascimento) dados.data_nascimento = data_nascimento
    if (nif) dados.nif = nif

    const { data, error } = await db
      .from('clientes')
      .upsert(dados, { onConflict: 'tenant_id,telefone', ignoreDuplicates: false })
      .select('id')
      .single()
    if (error) throw error
    return data.id
  })
}

const PAGE_SIZE = 15

const CAMPOS_CLIENTE_LISTA = 'id, nome, nome_amigavel, telefone, email, nif, sexo, data_nascimento, observacoes, morada, codigo_postal, ativo'

export async function pesquisarClientes(tenantId, termo, pagina = 0, apenasAtivos = true) {
  return comTenant(tenantId, async (db) => {
    const t = termo?.trim() || ''
    let query = db
      .from('clientes')
      .select(CAMPOS_CLIENTE_LISTA)
      .order('nome')
      .range(pagina * PAGE_SIZE, pagina * PAGE_SIZE + PAGE_SIZE - 1)
    if (apenasAtivos) query = query.eq('ativo', true)
    if (t.length > 0) {
      query = query.or(`nome.ilike.%${t}%,nome_amigavel.ilike.%${t}%,telefone.ilike.%${t}%,nif.ilike.%${t}%`)
    }
    const { data, error } = await query
    if (error) throw error
    return data
  })
}

// Variante paginada por páginas (Anterior/Seguinte): devolve { dados, total }.
// Usada na lista de Clientes do backoffice; `pesquisarClientes` (acima) continua
// a servir a pesquisa incremental com scroll da Nova Marcação.
export async function pesquisarClientesPaginado(tenantId, termo, pagina = 0, apenasAtivos = true, tamanhoPagina = 20) {
  return comTenant(tenantId, async (db) => {
    const t = termo?.trim() || ''
    let query = db
      .from('clientes')
      .select(CAMPOS_CLIENTE_LISTA, { count: 'exact' })
      .order('nome')
      .range(pagina * tamanhoPagina, pagina * tamanhoPagina + tamanhoPagina - 1)
    if (apenasAtivos) query = query.eq('ativo', true)
    if (t.length > 0) {
      query = query.or(`nome.ilike.%${t}%,nome_amigavel.ilike.%${t}%,telefone.ilike.%${t}%,nif.ilike.%${t}%`)
    }
    const { data, error, count } = await query
    if (error) throw error
    return { dados: data, total: count ?? 0 }
  })
}

export async function getClientes(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('clientes')
      .select('*')
      .order('nome')
    if (error) throw error
    return data
  })
}

// Busca uma única ficha de cliente por id. Necessário porque getClientes()
// está sujeito ao limite de 1000 linhas do Supabase — com muitos clientes,
// procurar por .find() na lista falhava para os que ficavam de fora.
export async function getCliente(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('clientes')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    if (error) throw error
    return data
  })
}

export async function createCliente(tenantId, { nome, telefone, email, data_nascimento, nif, sexo, observacoes, nome_amigavel, morada, codigo_postal }) {
  return comTenant(tenantId, async (db) => {
    const dados = { nome, telefone: normalizarTelefone(telefone), tenant_id: tenantId }
    if (email) dados.email = email
    if (data_nascimento) dados.data_nascimento = data_nascimento
    if (nif) dados.nif = nif
    if (sexo) dados.sexo = sexo
    if (observacoes) dados.observacoes = observacoes
    if (nome_amigavel) dados.nome_amigavel = nome_amigavel
    if (morada) dados.morada = morada
    if (codigo_postal) dados.codigo_postal = codigo_postal
    const { data, error } = await db
      .from('clientes')
      .insert(dados)
      .select('id')
      .single()
    if (error) throw error
    return data.id
  })
}

export async function updateCliente(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    // nome não é editável pelo backoffice — usar nome_amigavel para isso
    const { nome: _, ...dadosSeguros } = dados
    const { error } = await db
      .from('clientes')
      .update(dadosSeguros)
      .eq('id', id)
    if (error) throw error
  })
}

export async function getAgendamentosDoCliente(tenantId, clienteId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('agendamentos')
      .select('*, servicos(nome), profissionais(nome, cor_agenda)')
      .eq('cliente_id', clienteId)
      .order('data_hora_inicio', { ascending: false })
    if (error) throw error
    return data
  })
}

// Marcações de um cliente para a página de cobrança agrupada, com as suas
// parcelas (pagamentos). Traz TODAS as não-canceladas (passadas e futuras) — o
// frontend decide o que está por saldar a partir das parcelas. Devolve
// { agendamentos, pagamentos } para o cálculo (valoresAgendamento/estadoParcelas).
export async function getAgendamentosParaCobrar(tenantId, clienteId) {
  return comTenant(tenantId, async (db) => {
    const { data: agendamentos, error } = await db
      .from('agendamentos')
      .select('*, servicos(nome, preco), profissionais(nome)')
      .eq('cliente_id', clienteId)
      .neq('estado', 'cancelado')
      .order('data_hora_inicio', { ascending: false })
    if (error) throw error
    const ids = (agendamentos || []).map(a => a.id)
    let pagamentos = []
    if (ids.length > 0) {
      const { data: pags, error: e2 } = await db
        .from('pagamentos')
        .select('*')
        .in('agendamento_id', ids)
      if (e2) throw e2
      pagamentos = pags || []
    }
    return { agendamentos: agendamentos || [], pagamentos }
  })
}

// Cria uma cobrança agrupada (RPC `criar_cobranca`, migração 060): marca as
// parcelas indicadas como pagas (upsert) e regista o lote + itens, atomicamente.
// `itens` = [{ agendamento_id, tipo, descricao, valor }]. Devolve o id do lote.
export async function criarCobranca(tenantId, { clienteId, clienteNome, metodo, itens }, perfilNome) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('criar_cobranca', {
      p_tenant_id: tenantId,
      p_cliente_id: clienteId || null,
      p_cliente_nome: clienteNome || null,
      p_metodo: metodo || null,
      p_itens: itens,
      p_feito_por: perfilNome || null,
    })
    if (error) throw error
    return data // id da cobrança
  })
}

// Agendamentos de TODO o tenant (não só de um cliente) com data até `fimISO`
// — e, opcionalmente, a partir de `inicioISO` — para a marcação em massa de
// pagamentos de agendamentos antigos importados (Config Admin). Exclui
// cancelados. Devolve { agendamentos, pagamentos } tal como
// getAgendamentosParaCobrar, para reutilizar valoresAgendamento/estadoParcelas.
export async function getAgendamentosParaMarcarPagos(tenantId, inicioISO, fimISO) {
  return comTenant(tenantId, async (db) => {
    const TAMANHO_PAGINA = 1000
    let agendamentos = []
    let pagina = 0
    while (true) {
      const desde = pagina * TAMANHO_PAGINA
      const ate = desde + TAMANHO_PAGINA - 1
      let query = db
        .from('agendamentos')
        .select('id, cliente_id, cliente_nome, preco_total, data_hora_inicio, servicos(preco, sinal_valor)')
        .neq('estado', 'cancelado')
        .lte('data_hora_inicio', fimISO)
        .order('data_hora_inicio')
        .range(desde, ate)
      if (inicioISO) query = query.gte('data_hora_inicio', inicioISO)
      const { data, error } = await query
      if (error) throw error
      agendamentos = agendamentos.concat(data)
      if (data.length < TAMANHO_PAGINA) break
      pagina += 1
    }
    // Parcelas das marcações acima. O `.in(...)` vai por LOTES: com muitos
    // agendamentos antigos, enviar todos os ids num só filtro faz o URL do GET
    // rebentar (URL demasiado longo → net::ERR_FAILED/CORS). 200 ids por pedido
    // mantém o URL dentro dos limites.
    const ids = agendamentos.map(a => a.id)
    const LOTE_IN = 200
    let pagamentos = []
    for (let i = 0; i < ids.length; i += LOTE_IN) {
      const bloco = ids.slice(i, i + LOTE_IN)
      const { data: pags, error: e2 } = await db
        .from('pagamentos')
        .select('*')
        .in('agendamento_id', bloco)
      if (e2) throw e2
      if (pags) pagamentos = pagamentos.concat(pags)
    }
    return { agendamentos, pagamentos }
  })
}

// Emite o documento fiscal do SINAL cobrado no ato (Vendus). Só relevante com
// faturação Vendus activa; a Edge Function valida isso.
export async function emitirDocumentoSinal(tenantId, agendamentoId, { nif } = {}) {
  const payload = { agendamento_id: agendamentoId }
  if (nif != null) payload.nif = nif
  return chamarVendus('emitir_documento_sinal', tenantId, payload)
}

// Emite UM documento fiscal sobre uma seleção livre de parcelas já pagas
// (Edge Function `emitir_fatura_parcelas`). É o caminho único de emissão: serve
// o sinal (incluindo o pago por Stripe), o restante, um lote inteiro, ou a
// reemissão depois de um cancelamento. Quando os meios de pagamento diferem, o
// documento sai com o pagamento repartido.
export async function emitirFaturaParcelas(tenantId, pagamentoIds, { nif } = {}) {
  const payload = { pagamento_ids: pagamentoIds }
  if (nif) payload.nif = nif
  return chamarVendus('emitir_fatura_parcelas', tenantId, payload)
}

// Cancela a fatura de uma marcação — FASE 1: só do nosso lado (RPC
// `cancelar_fatura_agendamento`, migração 062). Limpa o ponteiro para o
// documento Vendus em todas as parcelas/marcações abrangidas pelo mesmo
// documento (um lote agrupado é um só documento), mantém-nas pagas (podem voltar
// a ser faturadas) e regista a nota nas observações. NÃO anula no Vendus (TODO
// fase 2 — o operador tem de o anular lá manualmente). Devolve
// { fatura_id, n_marcacoes, observacao }.
// `reabrirPagamentos` (opcional, default false): além de cancelar o documento,
// marca `pago = false` nas parcelas exatas que ele cobria — para quando a
// cobrança em si foi um engano, não só a fatura (migração 065).
export async function cancelarFaturaAgendamento(tenantId, agendamentoId, perfilNome, reabrirPagamentos = false) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('cancelar_fatura_agendamento', {
      p_tenant_id: tenantId,
      p_agendamento_id: agendamentoId,
      p_feito_por: perfilNome || null,
      p_reabrir_pagamentos: !!reabrirPagamentos,
    })
    if (error) throw error
    return data
  })
}

// Documentos fiscais emitidos num período, para o relatório de Faturação.
//
// Desde a 064 a fatura é uma entidade (`faturas`) com estado próprio, e as
// suas linhas (`faturas_linhas`) ligam parcela e marcação. Antes disto era
// preciso reconstruir o documento agrupando `pagamentos` pelo ponteiro
// `fatura_id`, e as canceladas viviam numa tabela à parte — agora é uma
// query só. O mapeamento para a forma do ecrã está em `lib/faturas.js`.
export async function getFaturas(tenantId, inicioISO, fimISO) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('faturas')
      .select(`
        id, vendus_id, numero, tipo, url, estado, emitida_em, emitida_por,
        cliente_id, cliente_nome, nif, metodo, total, cobranca_id,
        cancelada_em, cancelada_por, cancelada_motivo, nota_credito_id,
        faturas_linhas(
          agendamento_id, pagamento_id, descricao, profissional_nome,
          quando, parcela, valor,
          agendamentos(data_hora_inicio, estado, servicos(nome), profissionais(nome))
        )
      `)
      .gte('emitida_em', inicioISO)
      .lte('emitida_em', fimISO)
      .order('emitida_em', { ascending: false })
    if (error) throw error
    return mapearFaturas(data)
  })
}

// Fase 2: o documento foi mesmo anulado no Vendus (nota de crédito emitida).
// Fecha o ciclo — deixa de aparecer na lista do que falta lá anular.
export async function marcarFaturaAnuladaVendus(tenantId, vendusId, notaCreditoId, perfilNome) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('marcar_fatura_anulada_vendus', {
      p_tenant_id: tenantId,
      p_vendus_id: vendusId,
      p_nota_credito_id: notaCreditoId || null,
      p_feito_por: perfilNome || null,
    })
    if (error) throw error
    return data
  })
}

// Direito ao esquecimento (RGPD): apaga TODOS os agendamentos do cliente e
// depois o próprio cliente, de forma atómica (RPC `esquecer_cliente`, migração
// 052). Um DELETE simples na tabela `clientes` NÃO chegava — a FK é SET NULL e
// os dados pessoais ficavam nos agendamentos (cliente_nome/email/telefone/notas).
export async function deleteCliente(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db.rpc('esquecer_cliente', {
      p_tenant_id: tenantId,
      p_cliente_id: id,
    })
    if (error) throw error
    return Array.isArray(data) ? data[0] : data
  })
}

// -----------------------------------------------
// Perfis (RBAC)
// -----------------------------------------------
export async function getPerfis(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('perfis')
      .select('*, profissionais(id, nome, cor_agenda)')
      .order('criado_em')
    if (error) throw error
    return data
  })
}

export async function createPerfil(tenantId, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('perfis')
      .insert([{ ...dados, tenant_id: tenantId }])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

export async function updatePerfil(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('perfis')
      .update(dados)
      .eq('id', id)
    if (error) throw error
  })
}

export async function deletePerfil(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db.from('perfis').delete().eq('id', id)
    if (error) throw error
  })
}

export async function criarUtilizador(tenantId, dados) {
  const { data: { session } } = await supabase.auth.getSession()
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/criar-utilizador`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ ...dados, tenant_id: tenantId }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao criar utilizador')
  return result.data
}

// Repõe a password de um perfil para a inicial e força a sua mudança no
// próximo login. Acessível a managers e superusers (validado na Edge Function).
export async function reporPassword(tenantId, perfilId) {
  const { data: { session } } = await supabase.auth.getSession()
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/repor-password`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ perfil_id: perfilId, tenant_id: tenantId }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao repor a palavra-passe')
  return result.data
}

// -----------------------------------------------
// Promoções (campanhas por email)
// -----------------------------------------------
// Nº de clientes com opt-in e email (destinatários de uma campanha)
export async function contarClientesPromocao(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { count, error } = await db
      .from('clientes')
      .select('id', { count: 'exact', head: true })
      .eq('aceita_promocoes', true)
      .not('email', 'is', null)
    if (error) throw error
    return count || 0
  })
}

// Envia (ou testa) uma campanha via Edge Function autenticada
export async function enviarPromocao(tenantId, { assunto, html, teste = false, email_teste }) {
  const { data: { session } } = await supabase.auth.getSession()
  const resp = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/enviar_promocao`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ tenant_id: tenantId, assunto, html, teste, email_teste }),
    }
  )
  const result = await resp.json()
  if (!resp.ok) throw new Error(result.error || 'Erro ao enviar campanha')
  return result
}

// Unsubscribe público (página de cancelamento). RPC SECURITY DEFINER.
export async function cancelarPromocoesPublico(tenantId, email) {
  const { data, error } = await supabase.rpc('cancelar_promocoes_publico', {
    p_tenant_id: tenantId,
    p_email: email,
  })
  if (error) throw error
  return data // nº de registos afetados
}

// -----------------------------------------------
// Salas (capacidade por categoria)
// -----------------------------------------------
export async function getSalas(tenantId) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('salas')
      .select('*')
      .order('categoria')
      .order('criado_em')
    if (error) throw error
    return data
  })
}

export async function getSalasDaCategoria(tenantId, categoria) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('salas')
      .select('id, nome')
      .eq('categoria', categoria)
      .order('criado_em')
    if (error) throw error
    return data || []
  })
}

export async function escolherSalaLivre(tenantId, categoria, inicio, fim) {
  return comTenant(tenantId, async (db) => {
    const { data: salas, error: eS } = await db
      .from('salas')
      .select('id, nome')
      .eq('categoria', categoria)
      .order('criado_em')
    if (eS) throw eS
    if (!salas || salas.length === 0) return null

    // Ocupação do dia via função SECURITY DEFINER (anon não lê agendamentos).
    const inicioDia = new Date(inicio); inicioDia.setHours(0, 0, 0, 0)
    const fimDia = new Date(inicio); fimDia.setHours(23, 59, 59, 999)
    const { data: ocupacao, error } = await db.rpc('get_ocupacao_dia', {
      p_tenant_id: tenantId,
      p_inicio: inicioDia.toISOString(),
      p_fim: fimDia.toISOString(),
    })
    if (error) throw error

    return escolherSala(salas, ocupacao, categoria, inicio, fim)
  })
}

export async function createSala(tenantId, dados) {
  return comTenant(tenantId, async (db) => {
    const { data, error } = await db
      .from('salas')
      .insert([{ ...dados, tenant_id: tenantId }])
      .select()
      .single()
    if (error) throw error
    return data
  })
}

export async function updateSala(tenantId, id, dados) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db
      .from('salas')
      .update(dados)
      .eq('id', id)
    if (error) throw error
  })
}

export async function deleteSala(tenantId, id) {
  return comTenant(tenantId, async (db) => {
    const { error } = await db.from('salas').delete().eq('id', id)
    if (error) throw error
  })
}
